import FloorplanType from 'lib/floorplan';
import {
  FloorplanCoordinates,
  CADCoordinates,
  ImageCoordinates,
} from 'lib/geometry';
import AreaOfConcern from 'lib/area-of-concern';
import {
  ParsedPlanDXFAreaOfConcern,
  ParsedPlanDXFSpace,
  ParsedPlanDXFSensorPlacement,
} from 'lib/dxf';
import { distance } from 'lib/math';
import { LengthUnit, CONVERT_TO_METERS, Meters } from 'lib/units';
import PlanSensor from 'lib/sensor';
import Space from 'lib/space';

// How close do two sensors need to be to be considered at the same position?
//
// This value in a perfect world should be zero, but a non zero value allows for a little bit of
// inaccuracy in registration / in the DXF
const CAD_DISTANCE_EQUALITY_THRESHOLD_METERS = 0.05;

export const DEFAULT_CAD_FILE_LENGTH_UNIT: LengthUnit = 'inches';

// A FloorplanSensorChange represents an element of the diff between data currently on the floorplan, and
// data within a CAD file.
type FloorplanSensorAddition = {
  type: 'addition';
  data: ParsedPlanDXFSensorPlacement;
  snHardwareMatch?: null | boolean;
};
type FloorplanSensorDeletion = {
  type: 'deletion';
  data: PlanSensor;
  snHardwareMatch?: null | boolean;
};
type FloorplanSensorModification = {
  type: 'modification';
  data: ParsedPlanDXFSensorPlacement;
  oldData: PlanSensor;
  snHardwareMatch?: null | boolean;
};
type FloorplanSensorNoChange = {
  type: 'no-change';
  data: PlanSensor;
  snHardwareMatch?: null | boolean;
};

export type FloorplanSensorChange =
  | FloorplanSensorAddition
  | FloorplanSensorDeletion
  | FloorplanSensorModification
  | FloorplanSensorNoChange;

type FloorplanAreaOfConcernAddition = {
  type: 'addition';
  data: ParsedPlanDXFAreaOfConcern;
};
type FloorplanAreaOfConcernDeletion = {
  type: 'deletion';
  data: AreaOfConcern;
};
type FloorplanAreaOfConcernModification = {
  type: 'modification';
  data: ParsedPlanDXFAreaOfConcern;
  oldData: AreaOfConcern;
};
type FloorplanAreaOfConcernNoChange = {
  type: 'no-change';
  data: AreaOfConcern;
};

export type FloorplanAreaOfConcernChange =
  | FloorplanAreaOfConcernAddition
  | FloorplanAreaOfConcernDeletion
  | FloorplanAreaOfConcernModification
  | FloorplanAreaOfConcernNoChange;

type FloorplanSpaceAddition = {
  type: 'addition';
  data: ParsedPlanDXFSpace;
};
type FloorplanSpaceDeletion = {
  type: 'deletion';
  data: Space;
};
type FloorplanSpaceModification = {
  type: 'modification';
  data: ParsedPlanDXFSpace;
  oldData: Space;
};
type FloorplanSpaceNoChange = {
  type: 'no-change';
  data: Space;
};

export type FloorplanSpaceChange =
  | FloorplanSpaceAddition
  | FloorplanSpaceDeletion
  | FloorplanSpaceModification
  | FloorplanSpaceNoChange;

// The default CAD origin is in the lower left hand corner of the floorplan.
export function computeDefaultCADOrigin(
  floorplan: FloorplanType
): FloorplanCoordinates {
  const floorplanHeightInMeters = floorplan.height / floorplan.scale;
  return FloorplanCoordinates.create(0, floorplanHeightInMeters);
}

// Given an old floorplan and some metadata, compute the new "floorplan" object that contains scale,
// etc for rendering the new floorplan at it's native resolution.
export function computeNewFloorplanForCAD(
  oldFloorplan: FloorplanType,
  image: HTMLImageElement,
  floorplanCADOrigin: FloorplanCoordinates,
  cadFileUnit: LengthUnit,
  cadFileScale: number,
  imageResizeScale: number, // The scale factor applied to resize the image to be 4096 wide at max
  pixelsPerCadUnit: number
): FloorplanType {
  const coordA = CADCoordinates.toFloorplanCoordinates(
    CADCoordinates.create(0, 0),
    oldFloorplan,
    floorplanCADOrigin,
    cadFileUnit,
    cadFileScale
  );
  const coordB = CADCoordinates.toFloorplanCoordinates(
    CADCoordinates.create(1, 0),
    oldFloorplan,
    floorplanCADOrigin,
    cadFileUnit,
    cadFileScale
  );
  const metersPerCADUnit = coordB.x - coordA.x;
  const cadImageScale = 1 / metersPerCADUnit;
  const scale = cadImageScale * pixelsPerCadUnit * imageResizeScale;

  return {
    width: image.width,
    height: image.height,
    scale: scale,
    origin: ImageCoordinates.create(0, 0),
    rotation: 0,
  };
}

// Given a list of sensors on the floorplan and a list of sensor placements in the CAD file, compute
// the difference between the two. This function powers the "diff" seen in the CAD import process.
export function computeFloorplanSensorChanges(
  importSensors: boolean,
  keepExistingSensors: boolean,
  cadSensors: Array<ParsedPlanDXFSensorPlacement>,
  sensors: Array<PlanSensor>,
  oldFloorplan: FloorplanType,
  floorplanCADOrigin: FloorplanCoordinates,
  cadFileUnit: LengthUnit,
  cadFileScale: number
): Array<FloorplanSensorChange> {
  // If only the floorplan is being swapped, then gray out existing sensors
  if (!importSensors) {
    return sensors.map((data) => ({ type: 'no-change' as const, data }));
  }

  const newData = cadSensors.filter((s) => s.cadId.length > 0);
  const newDataCadIds = newData.map((s) => s.cadId);

  const oldData = sensors.filter((s) => s.cadId.length > 0);
  const oldDataCadIds = oldData.map((s) => s.cadId);

  // Get all new rows in `newData` that are not in `oldData` - these are additions.
  const additions = newData.filter((s) => !oldDataCadIds.includes(s.cadId));
  // Vice versa for deletions
  let deletions = oldData.filter((s) => !newDataCadIds.includes(s.cadId));
  let unmodified: Array<PlanSensor> = [];

  if (keepExistingSensors) {
    // If edge cases when we do not want to edit already existing cases,
    // the sensors that would normally be deleted will rather be "unmodified"
    unmodified = deletions;
    deletions = [];
  }

  const modifications: Array<ParsedPlanDXFSensorPlacement> = [];
  // So all other rows must be modifications
  const additionsAndDeletionsCadIds = [
    ...additions.map((s) => s.cadId),
    ...deletions.map((s) => s.cadId),
  ];

  for (const s of newData) {
    // Filter out additions / deletions
    if (additionsAndDeletionsCadIds.includes(s.cadId)) {
      continue;
    }

    // Find existing sensor row
    const existingSensor = oldData.find((i) => i.cadId === s.cadId);
    if (!existingSensor) {
      // This should be impossible, additions were filtered out above
      continue;
    }

    // Check to see if any fields aren't equal
    let isModification = false;
    for (const key of Object.keys(s)) {
      const castedKey = key as keyof ParsedPlanDXFSensorPlacement;

      // Position needs to be handled specially
      if (castedKey === 'position') {
        const cadCoord = FloorplanCoordinates.toCADCoordinates(
          FloorplanCoordinates.create(
            existingSensor.position.x,
            existingSensor.position.y
          ),
          oldFloorplan,
          floorplanCADOrigin,
          cadFileUnit,
          cadFileScale
        );
        const distanceInCadUnits = distance(cadCoord, s.position);
        const distanceInMeters =
          CONVERT_TO_METERS[cadFileUnit](distanceInCadUnits);

        if (distanceInMeters > CAD_DISTANCE_EQUALITY_THRESHOLD_METERS) {
          isModification = true;
          break;
        }
        continue;
      }

      if (
        JSON.stringify(s[castedKey]) !==
        JSON.stringify(existingSensor[castedKey])
      ) {
        isModification = true;
        break;
      }
    }
    if (isModification) {
      modifications.push(s);
    } else {
      unmodified.push(existingSensor);
    }
  }

  return [
    ...unmodified.map((data) => ({
      type: 'no-change' as const,
      data,
    })),
    ...modifications.map((data) => ({
      type: 'modification' as const,
      data,
      // NOTE: this will always exist, this search is already done above
      oldData: oldData.find((i) => i.cadId === data.cadId) as PlanSensor,
    })),
    ...additions.map((data) => ({ type: 'addition' as const, data })),
    ...deletions.map((data) => ({ type: 'deletion' as const, data })),

    // Existing sensors without a CAD ID should be deleted
    ...sensors
      .filter((s) => s.cadId.length === 0 && !keepExistingSensors)
      .map((data) => ({ type: 'deletion' as const, data })),
    // New sensors without a CAD ID should be added
    ...cadSensors
      .filter((s) => s.cadId.length === 0)
      .map((data) => ({ type: 'addition' as const, data })),
  ];
}

export function computeFloorplanAreasOfConcernChanges(
  importAreasOfConcern: boolean,
  cadAreasOfConcern: Array<ParsedPlanDXFAreaOfConcern>,
  areasOfConcern: Array<AreaOfConcern>,
  oldFloorplan: FloorplanType,
  floorplanCADOrigin: FloorplanCoordinates,
  cadFileUnit: LengthUnit,
  cadFileScale: number
): Array<FloorplanAreaOfConcernChange> {
  // The positions/area are displayed in sq inches in our diff table.
  if (!importAreasOfConcern) {
    return areasOfConcern.map((data) => ({ type: 'no-change' as const, data }));
  }

  cadAreasOfConcern = cadAreasOfConcern.map((aoc) => ({
    ...aoc,
    areaMeters: Meters.fromSquareInches(aoc.areaMeters),
  }));

  // Unlike the sensors, AOC support only replacement, i.e.,
  // old areas are replaced by new OR old areas stay and no new imported.
  const additions = cadAreasOfConcern;
  const deletions = areasOfConcern;

  const unmodified: Array<AreaOfConcern> = [];

  return [
    ...unmodified.map((data) => ({
      type: 'no-change' as const,
      data,
    })),
    ...additions.map((data) => ({ type: 'addition' as const, data })),
    ...deletions.map((data) => ({ type: 'deletion' as const, data })),
  ];
}

export function computeFloorplanSpacesChanges(
  importSpaces: boolean,
  cadSpaces: Array<ParsedPlanDXFSpace>,
  spaces: Array<Space>,
  oldFloorplan: FloorplanType,
  floorplanCADOrigin: FloorplanCoordinates,
  cadFileUnit: LengthUnit,
  cadFileScale: number
): Array<FloorplanSpaceChange> {
  // The positions/area are displayed in sq inches in our diff table.
  if (!importSpaces) {
    return spaces.map((data) => ({ type: 'no-change' as const, data }));
  }

  // Unlike the sensors, AOC support only replacement, i.e.,
  // old areas are replaced by new OR old areas stay and no new imported.
  const additions = cadSpaces;
  const deletions = spaces;

  const unmodified: Array<Space> = [];

  return [
    ...unmodified.map((data) => ({
      type: 'no-change' as const,
      data,
    })),
    ...additions.map((data) => ({ type: 'addition' as const, data })),
    ...deletions.map((data) => ({ type: 'deletion' as const, data })),
  ];
}
